import type {ReactElement, RefObject} from 'react';
import {useContext, useEffect, useRef} from 'react';
import type {Editor} from 'grapesjs';
import {formMakerContext} from '@FormMakerReact/form-maker.context';
import type {CanvasDevice} from '@FormMakerReact/form-maker.types';
import {EditorViewport} from '@FormMakerReact/form-maker.types';
import {FormMakerFormToggler} from '@FormMakerReact/layout/form-maker-form-toggler/form-maker-form-toggler';
import {CanvasLoadingSkeleton} from './components/canvas-loading-skeleton/canvas-loading-skeleton';
import {DevicePill} from './components/device-pill/device-pill';
import type {FormMakerCanvasProps} from './form-maker-canvas.types';
import styles from './form-maker-canvas.module.scss';
import '@FormMakerReact/styles/rte-toolbar.scss';

export function FormMakerCanvas({onContainerReady}: FormMakerCanvasProps): ReactElement {
  const {editor, state, actions} = useContext(formMakerContext);
  const ref = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (ref.current) {
      onContainerReady(ref.current);
    }
  }, [onContainerReady]);
  useRefreshCanvasToolsAfterDeviceTransition(containerRef, editor);
  return (
    <div className={styles.canvasArea}>
      {renderDevicePillExceptOnNonDesktop(state.editorViewport, state.canvasDevice, actions.setCanvasDevice)}
      <div className={`${styles.canvasContainer} ${styles[state.canvasDevice]}`} ref={containerRef}>
        <div className={`${styles.canvasMount} ${state.isEditorLoading ? styles.canvasMountHidden : ''}`} ref={ref} />
        {renderLoadingSkeleton(state.isEditorLoading)}
      </div>
      <FormMakerFormToggler />
    </div>
  );
}

function useRefreshCanvasToolsAfterDeviceTransition(containerRef: RefObject<HTMLDivElement | null>, editor: Editor | null): void {
  useEffect((): (() => void) | undefined => {
    const container = containerRef.current;
    if (!container || !editor) {
      return undefined;
    }

    const handleTransitionEnd = (event: TransitionEvent): void => {
      if (event.target !== container || event.propertyName !== 'max-width') {
        return;
      }
      editor.Canvas.refresh();
    };

    container.addEventListener('transitionend', handleTransitionEnd);
    return (): void => {
      container.removeEventListener('transitionend', handleTransitionEnd);
    };
  }, [containerRef, editor]);
}

function renderDevicePillExceptOnNonDesktop(editorViewport: EditorViewport, currentDevice: CanvasDevice, onSelect: (device: CanvasDevice) => void): ReactElement | null {
  if (editorViewport !== EditorViewport.DESKTOP) {
    return null;
  }
  return <DevicePill currentDevice={currentDevice} onSelect={onSelect} />;
}

function renderLoadingSkeleton(isLoading: boolean): ReactElement | null {
  if (!isLoading) {
    return null;
  }
  return <CanvasLoadingSkeleton />;
}
